import { useCallback } from "react";

import { useLibrary } from "../../../providers";
import {
  useRequestWithFeedback,
  type OverrideRunProps,
} from "../../../composites";

import { API } from "./api";

import type { UserDisplayPreferencesPayload } from "./types";

export const useGetUserDisplayPreferences = () => {
  const { send, loading } =
    useRequestWithFeedback<API.UserDisplayPreferences>();

  const getUserDisplayPreferences = useCallback(
    (overrideRunProps?: OverrideRunProps<API.UserDisplayPreferences>) => {
      send({
        action: API.getUserDisplayPreferences(),
        ...overrideRunProps,
      });
    },
    [send],
  );

  return { getUserDisplayPreferences, loading };
};

export const useCreateEditUserDisplayPreferences = () => {
  const l10n = useLibrary("translations");
  const { send, loading } =
    useRequestWithFeedback<API.UserDisplayPreferences>();

  const createEditUserDisplayPreferences = useCallback(
    (
      customizationData: UserDisplayPreferencesPayload,
      isEdit?: boolean,
      overrideRunProps?: OverrideRunProps<API.UserDisplayPreferences>,
    ) => {
      const method = isEdit
        ? API.editUserDisplayPreferences
        : API.createUserDisplayPreferences;
      send({
        action: method(customizationData),
        messaging: {
          toast: {
            success: l10n.getString(
              "dashboard-customization-create-edit-success",
              null,
            ),
            error: l10n.getString(
              "dashboard-customization-create-edit-fail",
              null,
            ),
          },
        },
        ...overrideRunProps,
      });
    },
    [l10n, send],
  );

  return { createEditUserDisplayPreferences, loading };
};

export const useDeleteUserDisplayPreferences = () => {
  const { send, loading } = useRequestWithFeedback<void>();

  const deleteUserDisplayPreferences = useCallback(
    (overrideRunProps?: OverrideRunProps<void>) => {
      send({
        action: API.deleteUserDisplayPreferences(),
        ...overrideRunProps,
      });
    },
    [send],
  );

  return { deleteUserDisplayPreferences, loading };
};
